import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearchPlus,
  faSpinner,
  faCheckCircle,
  faFileAlt,
  faBell,
  faQuestionCircle,
  faChevronDown,
  faChevronUp,
  faExclamationTriangle,
  faClock,
  faRedo,
  faRobot
} from '@fortawesome/free-solid-svg-icons';
import siemCorrelationService from '../../services/siemCorrelationService';
import { useLanguage } from '../../contexts/LanguageContext';

const VERDICT_ICONS = {
  not_detected: faCheckCircle,
  event_logged: faFileAlt,
  alert_generated: faBell,
  inconclusive: faQuestionCircle
};

const SIEMCorrelationSection = ({ action, isDarkMode, onRecheck }) => {
  const { t } = useLanguage();
  const [expandedIntegration, setExpandedIntegration] = useState(null);
  const [fullResults, setFullResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);

  const correlation = action?.siemCorrelation;
  if (!correlation || correlation.status === 'none') return null;

  const summary = correlation.summary || {};
  const verdictCfg = siemCorrelationService.getVerdictConfig(summary.detectionVerdict);
  const isRunning = correlation.status === 'pending' || correlation.status === 'running';

  const loadFullResults = async () => {
    if (fullResults || loadingResults) return;
    setLoadingResults(true);
    try {
      const data = await siemCorrelationService.getResults(action._id);
      setFullResults(data);
    } catch (err) {
      console.error('Error loading correlation results:', err);
    } finally {
      setLoadingResults(false);
    }
  };

  const toggleIntegration = (idx) => {
    if (expandedIntegration === idx) {
      setExpandedIntegration(null);
    } else {
      setExpandedIntegration(idx);
      if (!fullResults) loadFullResults();
    }
  };

  const statusBadge = () => {
    if (isRunning) {
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
          <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
          {t('siemCorrelation.statusRunning') !== 'siemCorrelation.statusRunning' ? t('siemCorrelation.statusRunning') : 'Checking...'}
        </span>
      );
    }
    if (correlation.status === 'failed') {
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700'}`}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          {t('common.error') || 'Failed'}
        </span>
      );
    }
    return null;
  };

  const results = fullResults?.results || correlation.results || [];

  return (
    <div className="mt-1">
      <h4 className={`text-sm font-semibold mb-2 flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        <FontAwesomeIcon icon={faSearchPlus} className="mr-2 text-teal-500" />
        {t('siemCorrelation.triggerTitle') !== 'siemCorrelation.triggerTitle' ? t('siemCorrelation.triggerTitle') : 'SIEM Detection Correlation'}
        <span className="ml-2">{statusBadge()}</span>
        {summary.autoUpdatedResult && (
          <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
            <FontAwesomeIcon icon={faRobot} className="mr-1" />
            {t('siemCorrelation.autoCorrelated') !== 'siemCorrelation.autoCorrelated' ? t('siemCorrelation.autoCorrelated') : 'Auto-correlated'}
          </span>
        )}
      </h4>

      <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        {/* Verdict */}
        {summary.detectionVerdict && !isRunning && (
          <div className="flex items-center justify-between mb-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${isDarkMode ? verdictCfg.bgDark : verdictCfg.bgLight}`}>
              <FontAwesomeIcon icon={VERDICT_ICONS[summary.detectionVerdict] || faQuestionCircle} className="mr-1.5" />
              {t(`siemCorrelation.verdict_${summary.detectionVerdict}`) !== `siemCorrelation.verdict_${summary.detectionVerdict}`
                ? t(`siemCorrelation.verdict_${summary.detectionVerdict}`)
                : verdictCfg.label}
            </span>
            {onRecheck && !isRunning && (
              <button
                onClick={onRecheck}
                className={`text-xs px-2 py-1 rounded transition-colors ${isDarkMode ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-blue-50'}`}
              >
                <FontAwesomeIcon icon={faRedo} className="mr-1" />
                {t('siemCorrelation.recheck') !== 'siemCorrelation.recheck' ? t('siemCorrelation.recheck') : 'Re-check'}
              </button>
            )}
          </div>
        )}

        {/* Summary stats */}
        {!isRunning && summary.totalIntegrationsQueried > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div className={`text-center p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{summary.totalIntegrationsQueried}</div>
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('siemCorrelation.integrationsQueried') !== 'siemCorrelation.integrationsQueried' ? t('siemCorrelation.integrationsQueried') : 'Integrations'}
              </div>
            </div>
            <div className={`text-center p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{summary.totalEventsFound}</div>
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('siemCorrelation.eventsFound') !== 'siemCorrelation.eventsFound' ? t('siemCorrelation.eventsFound') : 'Events Found'}
              </div>
            </div>
            <div className={`text-center p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{summary.totalAlertsMatched}</div>
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('siemCorrelation.alertsMatched') !== 'siemCorrelation.alertsMatched' ? t('siemCorrelation.alertsMatched') : 'Alerts Matched'}
              </div>
            </div>
            <div className={`text-center p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{summary.highestMatchScore}</div>
              <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('siemCorrelation.highestScore') !== 'siemCorrelation.highestScore' ? t('siemCorrelation.highestScore') : 'Highest Score'}
              </div>
            </div>
          </div>
        )}

        {/* Recommendation */}
        {summary.recommendation && !isRunning && (
          <div className={`text-sm p-2 rounded mb-3 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-600'}`}>
            <span className="font-medium">
              {t('siemCorrelation.recommendation') !== 'siemCorrelation.recommendation' ? t('siemCorrelation.recommendation') : 'Recommendation'}:
            </span>{' '}
            {summary.recommendation}
          </div>
        )}

        {/* Per-integration details (expandable) */}
        {!isRunning && results.length > 0 && (
          <div className="space-y-1">
            {results.map((result, idx) => (
              <div key={idx} className={`rounded border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => toggleIntegration(idx)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left ${isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      result.status === 'completed' ? 'bg-green-500' :
                      result.status === 'failed' ? 'bg-red-500' :
                      result.status === 'timeout' ? 'bg-yellow-500' :
                      'bg-gray-400'
                    }`} />
                    <span className="font-medium">{result.integrationName || result.siemType}</span>
                    <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      ({result.siemType})
                    </span>
                    {result.rawResultCount > 0 && (
                      <span className={`text-xs px-1.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                        {result.rawResultCount} events
                      </span>
                    )}
                    {result.error && (
                      <span className="text-xs text-red-500">{result.error}</span>
                    )}
                  </div>
                  <FontAwesomeIcon icon={expandedIntegration === idx ? faChevronUp : faChevronDown} className="text-xs" />
                </button>

                {expandedIntegration === idx && (
                  <div className={`px-3 pb-3 text-xs space-y-2 ${isDarkMode ? 'border-t border-gray-700' : 'border-t border-gray-200'}`}>
                    {result.queryUsed && (
                      <div className="mt-2">
                        <div className={`font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Query Used:</div>
                        <pre className={`p-2 rounded overflow-x-auto text-xs ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                          {result.queryUsed}
                        </pre>
                      </div>
                    )}
                    {result.queryDurationMs != null && (
                      <div className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>
                        <FontAwesomeIcon icon={faClock} className="mr-1" />
                        Query took {result.queryDurationMs}ms
                      </div>
                    )}
                    {result.matchedResults?.length > 0 && (
                      <div>
                        <div className={`font-medium mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Matched Events ({result.matchedResults.length}):
                        </div>
                        <div className="space-y-1">
                          {result.matchedResults.slice(0, 10).map((match, mi) => (
                            <div key={mi} className={`p-2 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-white'} border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{match.ruleName || 'Event'}</span>
                                <span className={`px-1.5 rounded text-xs font-bold ${
                                  match.matchScore >= 70 ? (isDarkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700') :
                                  match.matchScore >= 40 ? (isDarkMode ? 'bg-yellow-900/50 text-yellow-300' : 'bg-yellow-100 text-yellow-700') :
                                  (isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600')
                                }`}>
                                  Score: {match.matchScore}
                                </span>
                              </div>
                              {match.description && <div className={`mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{match.description}</div>}
                              {match.matchReasons?.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {match.matchReasons.map((reason, ri) => (
                                    <span key={ri} className={`px-1 rounded ${isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                      {reason}
                                    </span>
                                  ))}
                                </div>
                              )}
                              <div className={`mt-1 flex gap-3 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {match.timestamp && <span>{new Date(match.timestamp).toLocaleString()}</span>}
                                {match.destHost && <span>Host: {match.destHost}</span>}
                                {match.severity && <span>Severity: {match.severity}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Loading indicator for running */}
        {isRunning && (
          <div className="flex items-center justify-center py-4">
            <FontAwesomeIcon icon={faSpinner} spin className="text-blue-500 text-lg mr-2" />
            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
              {t('siemCorrelation.statusRunning') !== 'siemCorrelation.statusRunning' ? t('siemCorrelation.statusRunning') : 'Querying SIEM integrations...'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SIEMCorrelationSection;
